import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const LINES = [
    "Initializing Autonomous Processing Core",
    "Connecting Distributed Intelligence Nodes",
    "Synchronizing Memory Layer",
    "Calibrating Orchestration Fabric",
    "Verifying Sentinel Trust Chain",
    "Backbone Online",
];

export default function Loader({ onDone }) {
    const [idx, setIdx] = useState(0);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (idx < LINES.length) {
            const t = setTimeout(() => setIdx(i => i + 1), idx === 0 ? 700 : 520);
            return () => clearTimeout(t);
        } else {
            const t = setTimeout(() => setDone(true), 700);
            const t2 = setTimeout(() => onDone?.(), 1400);
            return () => { clearTimeout(t); clearTimeout(t2); };
        }
    }, [idx, onDone]);

    return (
        <AnimatePresence>
            {!done && (
                <motion.div
                    data-testid="apib-loader"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6 }}
                    className="fixed inset-0 z-[150] bg-void flex items-center justify-center scanlines"
                >
                    {/* Particle field */}
                    <ParticleField />

                    <div className="relative z-10 w-full max-w-2xl px-8">
                        {/* Glowing core dot */}
                        <motion.div
                            className="mx-auto mb-12 w-3 h-3 rounded-full bg-signal"
                            style={{ boxShadow: "0 0 60px 10px rgba(255,176,0,0.6), 0 0 160px 40px rgba(255,176,0,0.25)" }}
                            animate={{ scale: [1, 1.3, 1] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                        />

                        <div className="font-mono text-xs sm:text-sm space-y-2 text-ghost">
                            {LINES.slice(0, idx + 1).map((l, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -8 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.35 }}
                                    className={`flex items-center gap-3 ${i === idx ? "text-signal glow-amber tcur" : "text-muted"}`}
                                    data-testid={`loader-line-${i}`}
                                >
                                    <span className="text-signal/60">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-signal/60">›</span>
                                    <span className="tracking-tight">{l}{i < idx && " ... OK"}</span>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-12">
                            <div className="h-px bg-border w-full overflow-hidden relative">
                                <motion.div
                                    initial={{ scaleX: 0 }}
                                    animate={{ scaleX: (idx + 1) / LINES.length }}
                                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                    className="origin-left h-full bg-signal relative overflow-hidden"
                                >
                                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-sweep" />
                                </motion.div>
                            </div>
                            <div className="mt-2 flex justify-between text-[10px] font-mono text-muted uppercase tracking-[0.3em]">
                        <span>APIB</span>
                                <span>{Math.round(((idx + 1) / LINES.length) * 100)}%</span>
                            </div>
                        </div>

                        <button
                            data-testid="loader-skip"
                            onClick={() => { setDone(true); setTimeout(() => onDone?.(), 600); }}
                            className="absolute top-6 right-6 text-[10px] font-mono uppercase tracking-[0.3em] text-muted hover:text-signal transition-colors"
                        >
                            [esc] skip
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function ParticleField() {
    return (
        <svg className="absolute inset-0 w-full h-full opacity-60" preserveAspectRatio="xMidYMid slice">
            <defs>
                <radialGradient id="pg" cx="50%" cy="50%">
                    <stop offset="0%" stopColor="#FFB000" stopOpacity="1" />
                    <stop offset="100%" stopColor="#FFB000" stopOpacity="0" />
                </radialGradient>
            </defs>
            {Array.from({ length: 80 }).map((_, i) => {
                const x = (i * 137) % 100;
                const y = (i * 91) % 100;
                const r = 0.6 + (i % 4) * 0.4;
                const dur = 2 + (i % 5);
                return (
                    <circle
                        key={i}
                        cx={`${x}%`}
                        cy={`${y}%`}
                        r={r}
                        fill="url(#pg)"
                    >
                        <animate attributeName="opacity" values="0;1;0" dur={`${dur}s`} begin={`${i * 0.05}s`} repeatCount="indefinite" />
                    </circle>
                );
            })}
        </svg>
    );
}
